import { Button, useComputed } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { StepActions, useStepperContext } from "src/components/stepper";
import { BidContractType, BidPackageStatus, BidScopeSource, SaveBidPackageInput } from "src/generated/graphqlTypes";
import { useSaveBidPackagesMutation } from "src/routes/bids/add-bid-package/AddBidPackagePage.generated";
import { createBidPackagesDetailUrl, createBidPackagesUrl, createPlanBasedBidPackageEditUrl } from "src/RouteUrls";
import { fail, formatList } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";
import { INTERNAL_ESTIMATE_ID } from "../PlanBasedBidPackagePage";
import { PlanBasedBidPackageFormState, PlanBasedBidPackageStep } from "../utils";

type PlanBasedBidPackageStepActionsProps = { formState: ObjectState<PlanBasedBidPackageFormState>; isEdit: boolean };

export function PlanBasedBidPackageStepActions(props: PlanBasedBidPackageStepActionsProps) {
  const { formState, isEdit } = props;
  const { currentStep, nextStep, prevStep, setSteps, steps, isLastStep } = useStepperContext();
  const [{ projectId }] = useQueryParams({ projectId: StringParam });
  const [saveBidPackages, { loading: savingBidPackages }] = useSaveBidPackagesMutation();
  const navigate = useNavigate();
  const currentStepIndex = steps.findIndex((step) => step.value === currentStep.value);
  const nextStepDisabled = steps[currentStepIndex + 1]?.disabled;

  const save = useCallback(
    async (exiting: boolean) => {
      const input = mapToSaveBidPackagesInput(formState);
      const result = await saveBidPackages({ variables: { input: [input] } });
      const saved = result.data?.saveBidPackages.bidPackages?.first;
      const groupId = saved?.bidPackageGroup?.id;
      if (groupId) {
        const requests = (formState.value.requests ?? []).map((r) => ({
          ...r,
          // Pick up assigned IDs from saved requests
          id: r.id ?? saved?.requests?.find((sr) => sr.tradePartner.id === r.tradePartnerId)?.id,
        }));
        const bidPlanGroups = (formState.value.bidPlanGroups ?? [])
          // Drop archived rows from form state, the BE just soft-deleted them. To re-add, the FE must send a fresh (no ID) row
          .filter((g) => !g.archive)
          .map((g) => {
            // Fill in the ids the server just assigned, leave the rest of the row state alone.
            // Match by `order` (globally unique) rather than globalOptionId, since multiple base
            // groups all have a null globalOptionId and would otherwise collide.
            const savedGroup = saved?.bidPlanGroups?.find((sg) => sg.order === g.order);
            return {
              ...g,
              id: g.id ?? savedGroup?.id,
              dimensions: (g.dimensions ?? []).map((d) => ({
                ...d,
                id:
                  d.id ??
                  savedGroup?.dimensions.find(
                    (sd) =>
                      (d.globalOptionTypeId && sd.globalOptionType?.id === d.globalOptionTypeId) ||
                      (d.globalOptionGroupId && sd.globalOptionGroup?.id === d.globalOptionGroupId),
                  )?.id,
              })),
            };
          });
        formState.set({
          ...formState.value,
          bidPackageGroupId: groupId,
          bidPackageId: saved!.id,
          requests,
          bidPlanGroups,
        });
        // Commit changes so form state is no longer dirty and the stepper doesn't prompt about unsaved changes
        formState.commitChanges();
        window.history.replaceState(null, "", createPlanBasedBidPackageEditUrl(groupId, projectId));
      }
      setSteps((prev) => prev.map((s) => (s.value === currentStep.value ? { ...s, state: "complete" } : s)));
      if (exiting) {
        const url = isEdit ? createBidPackagesDetailUrl(groupId!, saved!.id) : createBidPackagesUrl(projectId);
        navigate(url);
      } else {
        nextStep();
      }
    },
    [formState, saveBidPackages, setSteps, projectId, currentStep.value, isEdit, navigate, nextStep],
  );

  const submitBidPackage = useCallback(async () => {
    const bidPackageId = formState.value.bidPackageId ?? fail("Cannot submit a bid package that has not been saved");
    const groupId = formState.value.bidPackageGroupId ?? fail("Cannot submit a bid package that has not been saved");
    await saveBidPackages({ variables: { input: [{ id: bidPackageId, status: BidPackageStatus.Open }] } });
    navigate(createBidPackagesDetailUrl(groupId, bidPackageId));
  }, [formState, saveBidPackages, navigate]);

  const isFormValid = useComputed(() => formState.valid, [formState]);
  const continueDisabledReason = nextStepDisabled ? continueReasonByStep(currentStep.value, formState) : undefined;

  return (
    <StepActions>
      <>
        {currentStepIndex > 0 && <Button variant="tertiary" label="Back" size="lg" onClick={prevStep} />}
        <Button
          variant="secondary"
          label={isEdit ? "Save & Exit" : "Save Draft & Exit"}
          size="lg"
          onClick={() => save(true)}
          disabled={savingBidPackages}
        />
        {isLastStep ? (
          !isEdit && (
            <Button
              label="Create Bid Package"
              size="lg"
              onClick={submitBidPackage}
              disabled={savingBidPackages || currentStep.disabled || !isFormValid}
            />
          )
        ) : (
          <Button
            label="Continue"
            size="lg"
            onClick={() => save(false)}
            disabled={savingBidPackages || continueDisabledReason}
          />
        )}
      </>
    </StepActions>
  );
}

function continueReasonByStep(step: string, formState: ObjectState<PlanBasedBidPackageFormState>): string | undefined {
  if (step === PlanBasedBidPackageStep.DEFINE_PACKAGE) {
    return "Fill all required fields to continue.";
  }
  if (step === PlanBasedBidPackageStep.CONFIGURE_BID_GROUPS) {
    return formatList(formState.bidPlanGroups.errors);
  }
  return undefined;
}

function mapToSaveBidPackagesInput(formState: ObjectState<PlanBasedBidPackageFormState>): SaveBidPackageInput {
  const {
    bidPackageId,
    bidPackageGroupId,
    marketId,
    projects,
    costCodes,
    costType,
    internalEstimateType,
    productOfferings,
    requests,
    bidPlanGroups,
  } = formState.value;
  const internalEstimate = requests?.find((r) => r.tradePartnerId === INTERNAL_ESTIMATE_ID);
  return {
    id: bidPackageId ?? undefined,
    bidPackageGroupId: bidPackageGroupId ?? undefined,
    projects: marketId && (projects ?? []).isEmpty ? [marketId] : (projects ?? []),
    costCodes: costCodes ?? [],
    costType: costType ?? undefined,
    scopeSource: BidScopeSource.ProductOffering,
    isUnitBased: false,
    isInternalEstimate: false,
    isPlanBasedV2: true,
    internalEstimateBidContract: internalEstimate
      ? {
          id: internalEstimate.id,
          isInternalEstimate: true,
          internalEstimateType,
          archive: internalEstimate.archive,
          type: BidContractType.UserCreatedGroupBased,
        }
      : undefined,
    productOfferings: productOfferings ?? undefined,
    requests: requests?.nonEmpty ? requests.filter((r) => r.tradePartnerId !== INTERNAL_ESTIMATE_ID) : undefined,
    bidPlanGroups: bidPlanGroups
      ?.filter((g) => !g.archive)
      .map((g) => ({
        id: g.id ?? undefined,
        order: g.order ?? undefined,
        globalOptionId: g.globalOptionId ?? undefined,
        dimensions: (g.dimensions ?? []).map((d) => ({
          id: d.id ?? undefined,
          // Personalization (group-based) dimensions carry the global option group;
          // type-based ones leave it undefined.
          globalOptionGroupId: d.globalOptionGroupId ?? undefined,
          globalOptionTypeId: d.globalOptionTypeId ?? undefined,
          globalOptionIds: d.globalOptionIds ?? [],
        })),
      })),
  };
}
